<template>
  <el-dialog
    :visible.sync="innerVisible"
    width="1000px"
    :custom-class="'ie-form-dialog'"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    top="5vh"
    @closed="handleClosed"
  >
    <div class="fd-head">
      <span class="fd-ico">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path :d="isAdd ? 'M12 5v14M5 12h14' : 'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z'" />
        </svg>
      </span>
      <div class="fd-title">
        <span>{{ isAdd ? '新增 IE 监控' : '编辑 IE 主体' }}</span>
        <span class="fd-pt">{{ isAdd ? 'Novo Monitoramento de IE' : 'Editar Entidade de IE' }}</span>
      </div>
    </div>

    <div class="fd-body">
      <!-- 新增：录入后即加入监控；编辑：需先停用 -->
      <div v-if="isAdd" class="alert alert-info">
        <svg
          class="ico"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 7.4v5.6" />
          <circle
            cx="12"
            cy="16.6"
            r="1.15"
            fill="currentColor"
            stroke="none"
          />
        </svg>
        <span class="alert-text">
          <span>录入后即加入监控列表，状态默认"未知"。系统会在 5 分钟内发起首次查询。</span>
          <span class="alert-pt">
            Após o cadastro, entra na lista de monitoramento com status padrão "Desconhecido". A primeira consulta ocorre em até 5 minutos.
          </span>
        </span>
      </div>
      <div v-else class="alert alert-warn">
        <svg
          class="ico"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <span class="alert-text">
          <span>编辑前请先在 IE 信息管理列表将该主体<b>停用监控</b>，确保定时查询不会在保存中途拉到旧数据。</span>
          <span class="alert-pt">
            Antes de editar, primeiro defina esta entidade como <b>Desativar Monitoramento</b>, garantindo que a verificação agendada não leia dados desatualizados durante o salvamento.
          </span>
        </span>
      </div>

      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="top"
        size="small"
      >
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item prop="uf">
              <template slot="label">
                <bi-label zh="UF" inline />
              </template>
              <el-select
                v-if="isAdd"
                v-model="form.uf"
                placeholder="请选择州 / Selecione o estado"
                style="width: 100%"
              >
                <el-option
                  v-for="uf in ufOptions" :key="uf.id" :label="uf.label"
                  :value="uf.label"
                />
              </el-select>
              <el-input v-else v-model="form.uf" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="cnpj">
              <template slot="label">
                <bi-label zh="CNPJ" inline />
              </template>
              <el-input v-model="form.cnpj" placeholder="00.000.000/0000-00" />
              <div class="hint">
                14 位数字，含校验位
                <span class="hint-pt">/ 14 dígitos, incluindo dígitos verificadores</span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item prop="ie">
              <template slot="label">
                <bi-label zh="IE" inline />
              </template>
              <el-input v-model="form.ie" placeholder="州注册号 / Nº de registro estadual" />
              <div class="hint">
                9~14 位数字
                <span class="hint-pt">/ 9 a 14 dígitos</span>
              </div>
            </el-form-item>
          </el-col>
          <el-col v-if="!isAdd" :span="12">
            <el-form-item prop="companyName">
              <template slot="label">
                <bi-label zh="公司名称" pt="Razão Social" inline />
              </template>
              <el-input v-model="form.companyName" :disabled="!isAdd" placeholder="Razão Social" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item prop="city">
              <template slot="label">
                <bi-label zh="城市" pt="Cidade" inline />
              </template>
              <el-input v-model="form.city" placeholder="Município（可选）" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="bairro">
              <template slot="label">
                <bi-label zh="区" pt="Bairro" inline />
              </template>
              <el-input v-model="form.bairro" placeholder="Bairro" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="numero">
              <template slot="label">
                <bi-label zh="门牌号 Nº" pt="Nº (Número)" inline />
              </template>
              <el-input v-model="form.numero" placeholder="Nº" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="logradouro">
              <template slot="label">
                <bi-label zh="街道" pt="Logradouro" inline />
              </template>
              <el-input v-model="form.logradouro" placeholder="Ex: Av. Paulista, Rua das Flores" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item prop="validade">
              <template slot="label">
                <bi-label zh="有效期限" pt="Validade" inline />
              </template>
              <el-date-picker
                v-model="form.validade"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="YYYY-MM-DD"
                style="width: 100%"
              />
              <div class="hint">
                {{ validadeHint }}
                <span class="hint-pt">/ {{ validadeHintPt }}</span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item v-if="isAdd" prop="enable">
          <template slot="label">
            <bi-label zh="启用监控" pt="Ativar Monitoramento" inline />
          </template>
          <div class="enable-row">
            <el-switch v-model="form.enable" />
            <span class="enable-tip">
              停用后该 IE 不参与定时查询及告警
              <span class="hint-pt">
                / Após a desativação, esta IE não participa das consultas agendadas nem dos alertas
              </span>
            </span>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer">
      <el-button size="small" @click="innerVisible = false">
        <bi-label zh="取消" pt="Cancelar" inline />
      </el-button>
      <el-button size="small" type="primary" @click="handleSave">
        {{ isAdd ? '保存并启动监控' : '保存修改' }}
        <span class="btn-pt">/ {{ isAdd ? 'Salvar e Ativar Monitoramento' : 'Salvar Alterações' }}</span>
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import BiLabel from './BiLabel';
import { nowStamp, CNPJ_INPUT_RE, fmtCnpj } from '../mock';

const digitsOf = v => String(v || '').replace(/\D/g, '');

export default {
  name: 'IeFormDialog',
  components: { BiLabel },
  props: {
    visible: { type: Boolean, default: false },
    // add：新增 IE 监控；edit：编辑 IE 主体（仅停用时可编辑）
    mode: { type: String, default: 'add' },
    row: { type: Object, default: null },
    stateList: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: {
        id: null,
        uf: '',
        cnpj: '',
        ie: '',
        companyName: '',
        tradeName: '',
        city: '',
        cityCode: '',
        bairro: '',
        logradouro: '',
        numero: '',
        complemento: '',
        cep: '',
        phone: '',
        validade: '',
        enable: true
      },
      ufOptions: this.stateList,
      rules: {
        uf: [{ required: true, message: '请选择州 / Selecione o estado', trigger: 'change' }],
        cnpj: [
          { required: true, message: 'CNPJ 必填 / CNPJ obrigatório', trigger: ['blur', 'change'] },
          {
            validator: (r, v, cb) => (CNPJ_INPUT_RE.test(String(v || '').trim())
              ? cb()
              : cb(new Error('格式应为 00.000.000/0000-00 或 14 位数字 / Formato: 00.000.000/0000-00 ou 14 dígitos'))),
            trigger: ['blur', 'change']
          }
        ],
        ie: [
          { required: true, message: 'IE 必填 / IE obrigatório', trigger: ['blur', 'change'] },
          {
            validator: (r, v, cb) => {
              const n = digitsOf(v).length;
              return n >= 9 && n <= 14 ? cb() : cb(new Error('需 9~14 位数字 / Requer 9 a 14 dígitos'));
            },
            trigger: ['blur', 'change']
          }
        ],
        companyName: [{ required: true, message: '公司名称必填 / Razão Social obrigatória', trigger: ['blur', 'change'] }]
      }
    };
  },
  computed: {
    /** 弹窗显隐：与父组件 visible 双向绑定（支持 v-model / .sync） */
    innerVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    /** 是否新增模式；false 为编辑模式（此时 UF 只读） */
    isAdd() {
      return this.mode === 'add';
    },
    /** 有效期限字段说明（中文），新增 / 编辑文案略有差异 */
    validadeHint() {
      return this.isAdd
        ? '来源：税务侧 / 第三方风控公司（IFA）核查结果；系统不做自动获取与校验，仅作到期提醒参考。'
        : '来源：税务侧 / 第三方风控公司（IFA）；未填不参与到期提醒';
    },
    /** 有效期限字段说明（葡语） */
    validadeHintPt() {
      return this.isAdd
        ? 'Fonte: resultado da verificação do fisco / empresa terceira de risco (IFA); o sistema não obtém nem valida automaticamente — serve apenas como referência para o lembrete de vencimento.'
        : 'Fonte: fisco / empresa terceira de risco (IFA); se não preenchido, não participa do lembrete de vencimento';
    }
  },
  watch: {
    /** 监听显隐：每次打开都重新初始化表单（避免残留上次的输入与校验态） */
    visible(v) {
      if (v) this.initForm();
    }
  },
  methods: {
    /** 弹窗打开时初始化表单：编辑模式回填当前行（字段与接口一致），新增模式留空且默认启用监控 */
    initForm() {
      const r = this.row || {};
      this.form = {
        id: r.id || null,
        uf: r.uf || '',
        // 回填按展示格式（00.000.000/0000-00），提交时再去掉符号
        cnpj: fmtCnpj(r.cnpj) || '',
        ie: r.ie || '',
        companyName: r.companyName || '',
        tradeName: r.tradeName || '',
        city: r.city || '',
        cityCode: r.cityCode || '',
        bairro: r.bairro || '',
        logradouro: r.logradouro || '',
        numero: r.numero || '',
        complemento: r.complemento || '',
        cep: r.cep || '',
        phone: r.phone || '',
        validade: r.validade || '',
        // enable：0停用 1启用
        enable: r.enable === 1
      };
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate());
    },
    /** 关闭动画结束后同步 visible，保证父组件的 v-model / .sync 生效 */
    handleClosed() {
      this.$emit('update:visible', false);
    },
    /** 提交：校验通过后把表单 + 当前时间戳抛给父组件，再关闭弹窗 */
    handleSave() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return;
        const f = { ...this.form };
        this.$emit('save', {
          mode: this.isAdd ? 'add' : 'edit',
          form: f,
          // 新增/编辑后统一刷新「最后检查时间」为当前时间
          time: nowStamp()
        });
        this.innerVisible = false;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.fd-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}
.fd-ico {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #e8f7f0;
  color: #00a551;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fd-title {
  display: flex;
  flex-direction: column;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.fd-title .fd-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}

.alert {
  padding: 10px 14px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 16px;
}
.alert-info {
  background: #e8f0fe;
  color: #165dff;
  border: 1px solid #c7dbff;
}
.alert-warn {
  background: #fff7e8;
  color: #ad6800;
  border: 1px solid #ffe7ba;
}
.alert .ico {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
}
.alert-text {
  display: flex;
  flex-direction: column;
  line-height: 1.6;
}
.alert-text .alert-pt {
  font-size: 12px;
  color: #5c6b7a;
}

.hint {
  font-size: 12px;
  color: #86909c;
  line-height: 1.5;
  margin-top: 4px;
}
.hint-pt {
  font-size: 12px;
  color: #86909c;
}
.enable-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.enable-tip {
  font-size: 12px;
  color: #4e5969;
  line-height: 1.5;
}
.btn-pt {
  font-size: 12px;
}

::v-deep .ie-form-dialog .el-dialog__header { display: none; }
::v-deep .ie-form-dialog .el-dialog__body { padding: 20px; }
::v-deep .ie-form-dialog .el-form-item {
  margin-bottom: 20px;
}
</style>
